// Pulse / Tasks section — unified "what needs your attention" list.
//
// Backed by /api/v2/console/pulse/tasks (work item: PR B). Each row is
// one of four task kinds from the recently-shipped state machines:
//
//   - overdue_invoice  (3.11 sweep flipped state → 'overdue')
//   - expiring_quote   (sent/viewed quote with validUntil ≤ 7d away)
//   - failed_payment   (3.5 webhook, last 14d)
//   - disputed_payment (3.5 webhook, all-time)
//
// Each row is clickable and deep-links into the relevant detail page
// (uses `href` from the backend response, prefixed with the current
// business path).

import React, { useContext } from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { useNavigate } from 'react-router-dom';
import SectionShell from './SectionShell';
import KpiTile from './KpiTile';
import { BusinessContext } from '../../../contexts/BusinessContext';

const KIND_ICON = {
  overdue_invoice: ReceiptLongOutlinedIcon,
  expiring_quote: RequestQuoteOutlinedIcon,
  failed_payment: ErrorOutlineOutlinedIcon,
  disputed_payment: GavelOutlinedIcon,
};

const KIND_COLOR = {
  overdue_invoice: 'error.main',
  expiring_quote: 'warning.main',
  failed_payment: 'error.main',
  disputed_payment: 'error.main',
};

function formatDue(iso, overdue) {
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return '—';
  const today = new Date();
  const sameDay =
    due.getFullYear() === today.getFullYear() &&
    due.getMonth() === today.getMonth() &&
    due.getDate() === today.getDate();
  if (overdue) {
    const days = Math.round((today.getTime() - due.getTime()) / 86400000);
    if (days <= 0) return 'Overdue';
    return `Overdue ${days}d`;
  }
  if (sameDay) return 'Due today';
  return `Due ${due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

export default function TasksSection({ tasks }) {
  const { business } = useContext(BusinessContext);
  const navigate = useNavigate();
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  if (!tasks) {
    return (
      <SectionShell
        title="Tasks"
        subtitle="What needs your attention"
        icon={<ChecklistOutlinedIcon />}
        empty
        emptyCopy="Overdue invoices, expiring quotes, and failed payments will surface here as they happen."
      />
    );
  }

  if (tasks.openCount === 0) {
    return (
      <SectionShell
        title="Tasks"
        subtitle="What needs your attention"
        icon={<ChecklistOutlinedIcon />}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
          }}
        >
          <KpiTile value={0} label="Open" />
          <KpiTile value={0} label="Overdue" />
        </Box>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', textAlign: 'center', mt: 1 }}
        >
          Inbox zero. Nothing needs your attention right now.
        </Typography>
      </SectionShell>
    );
  }

  return (
    <SectionShell
      title="Tasks"
      subtitle="What needs your attention"
      icon={<ChecklistOutlinedIcon />}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
        }}
      >
        <KpiTile value={tasks.openCount} label="Open" />
        <KpiTile
          value={tasks.overdueCount}
          label="Overdue"
          accent={tasks.overdueCount > 0 ? 'error.main' : 'text.primary'}
        />
      </Box>

      <Divider sx={{ mt: 0.5 }} />

      <Stack divider={<Divider />} spacing={0}>
        {tasks.recent.slice(0, 4).map((t) => {
          const Icon = KIND_ICON[t.kind] || ChecklistOutlinedIcon;
          const iconColor = KIND_COLOR[t.kind] || 'text.secondary';
          return (
            <ButtonBase
              key={t.id}
              onClick={() => navigate(`${basePath}${t.href}`)}
              sx={{
                py: 1,
                px: 0.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                borderRadius: 1,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ minWidth: 0, flex: 1 }}
              >
                <Icon sx={{ color: iconColor, fontSize: 18 }} />
                <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.title}
                  </Typography>
                  {t.subtitle ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t.subtitle}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
              <Chip
                label={formatDue(t.dueAt, t.overdue)}
                size="small"
                color={t.overdue ? 'error' : 'default'}
                variant="outlined"
                sx={{
                  flexShrink: 0,
                  height: 22,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            </ButtonBase>
          );
        })}
      </Stack>
    </SectionShell>
  );
}
